import { createContext, useState, useContext, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { AuthSession } from './types';
import { http } from './http';

interface AuthContextType {
  session: AuthSession | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (tableName: string, operation: 'Select' | 'Insert' | 'Update' | 'Delete') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const checkSession = useCallback(async () => {
    try {
      const currentSession = await http.get<AuthSession>('/auth/session');
      setSession(currentSession);
    } catch (error) {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email: string, password: string) => {
    const newSession = await http.post<AuthSession>('/auth/login', { email, password });
    setSession(newSession);
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  };

  const logout = async () => {
    try {
      await http.post('/auth/logout');
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setSession(null);
      navigate('/login', { replace: true });
    }
  };

  const hasPermission = useCallback((tableName: string, operation: 'Select' | 'Insert' | 'Update' | 'Delete'): boolean => {
    if (!session?.permissions) {
      return false;
    }
    const permission = session.permissions[tableName];
    if (!permission) {
      return false;
    }
    const operationKey = `sta${operation}` as keyof typeof permission;
    return permission[operationKey] === 1;
  }, [session]);

  const value = useMemo(() => ({
    session,
    isLoading,
    login,
    logout,
    hasPermission,
  }), [session, isLoading, login, logout, hasPermission]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}